/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import React, { useEffect, useRef } from 'react'
import colors from '@sportninja/common/constants/appColors'
import Text from 'src/components/ts-components/Text'
import { useScrollSpyContext } from './ScrollSpyContext'

interface SectionProps {
  /** Must match an item key passed to ScrollSpyLayout. */
  id: string
  title: string
  /** Optional Title-case icon shown left of the title, tinted PRIMARY. */
  icon?: React.ReactNode
  /** Optional element pinned to the right edge of the title row (e.g. a chip). */
  titleAccessory?: React.ReactNode
  children: React.ReactNode
}

const Section = ({
  id,
  title,
  icon,
  titleAccessory,
  children,
}: SectionProps) => {
  const { registerSection } = useScrollSpyContext()
  const ref = useRef<HTMLElement | null>(null)

  // Registers this section's element for scroll-spy on mount and unregisters on
  // unmount. Assumes the element is stable for the component's lifetime.
  useEffect(() => {
    registerSection(id, ref.current)
    return () => registerSection(id, null)
  }, [id, registerSection])

  return (
    <section ref={ref} css={sectionStyle} data-testid={`section-${id}`}>
      <div css={titleRowStyle} aria-label={title}>
        {icon ? <span css={iconStyle}>{icon}</span> : null}
        {/* Title Case, per Figma 20023:1967 — not all-caps. */}
        <Text variant='paragraphMedium' weight='bold' color={colors.NEUTRAL_0}>
          {title}
        </Text>
        {titleAccessory ? (
          <span css={titleAccessoryStyle}>{titleAccessory}</span>
        ) : null}
      </div>
      <div css={fieldsStyle}>{children}</div>
    </section>
  )
}

export default Section

// ---- styled ----
const sectionStyle = css`
  scroll-margin-top: 24px;
  padding-bottom: 32px;
  margin-bottom: 32px;
  border-bottom: 1px solid ${colors.SOFT_STEEL};

  &:last-of-type {
    border-bottom: 0;
    margin-bottom: 0;
  }
`

const titleRowStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
`

// Pins the accessory (e.g. the Player ID chip) to the right edge of the row.
const titleAccessoryStyle = css`
  margin-left: auto;
  flex-shrink: 0;
`

// Tints the section icon PRIMARY and sizes it to the 32px Figma icon box.
const iconStyle = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${colors.PRIMARY};
  font-size: 24px;

  svg {
    width: 24px;
    height: 24px;
  }
`

const fieldsStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
`
